import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';

import { InputLabel } from 'src/web/components/InputLabel/component';
import {
  DropdownIndicator,
  MenuList,
  Option,
  ValueContainer,
} from '../common/components';

import { selectStyles } from '../common/styles';
import { OptionType, MultiSelectProps } from '../types';

export const MultiSelect = <OT extends OptionType>(
  props: MultiSelectProps<OT>
): React.ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);
  const isSavingRef = useRef(false);
  const {
    label,
    components,
    OptionContent,
    value,
    setValues,
    defaultValue,
    ...restProps
  } = props;
  const { maxItemsSelected } = restProps;

  const [input, setInput] = useState('');
  const [currentSelected, setCurrentSelected] = useState(
    (value || defaultValue || []) as readonly OT[]
  );
  const [newSelected, setNewSelected] = useState(
    (value || defaultValue || []) as readonly OT[]
  );

  useEffect(() => {
    if (value) {
      setCurrentSelected(value as readonly OT[]);
      setNewSelected(value as readonly OT[]);
    }
  }, [value]);

  const ariaId = uniqueId('multi-select-');
  const ariaProps = { 'aria-label': ariaId };

  return (
    <>
      {label && <InputLabel htmlFor={ariaId}>{label}</InputLabel>}
      <Select
        {...ariaProps}
        isMulti
        ref={selectRef}
        styles={selectStyles}
        value={newSelected}
        components={{
          DropdownIndicator,
          MenuList,
          Option,
          ValueContainer,

          // Note that passing additional components (like Option Content)
          // does work, but it's hard to make it compliant with TS.
          // Hence the ts-ignore comment.
          //
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          OptionContent,

          ...components,
        }}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={false}
        isOptionDisabled={(option: OT) => newSelected?.includes(option)}
        onChange={(values) => {
          if (
            maxItemsSelected === undefined ||
            values.length <= maxItemsSelected
          ) {
            setNewSelected(values);
          }
        }}
        inputValue={input}
        onInputChange={(value, action) => {
          if (action.action === 'input-change') {
            setInput(value);
          }
        }}
        onMenuClose={() => {
          setInput('');

          // This prevents newSelected being reset to the (previous) currentSelected, as onSave will also trigger onMenuClose.
          if (isSavingRef.current) {
            isSavingRef.current = false;
          } else {
            setNewSelected(currentSelected);
          }
        }}
        onSave={() => {
          setInput('');

          // This tells onMenuClose that the menu is closing because it's being saved, not because the user cancelled their selection.
          isSavingRef.current = true;
          setCurrentSelected(newSelected);

          if (setValues) {
            setValues(newSelected as OT[]);
          }

          if (selectRef) {
            selectRef.current.select?.blur();
          }
        }}
        onSelectAll={() => {
          if (props.options) {
            if (maxItemsSelected) {
              setNewSelected(props.options?.slice(0, maxItemsSelected) as OT[]);
            } else {
              setNewSelected(props.options as OT[]);
            }
          }
        }}
        onClearAll={() => {
          setNewSelected([]);
        }}
        {...restProps}
      />
    </>
  );
};
